import get from "lodash/get";
import moment from "moment";
import i18n from "../../../i18n";
import ActivityDrawer from "../../../parts/activity-drawer/ActivityDrawer";
import FlexibleProgram from "./FlexibleProgram";
import { TEMPLATE_PROJECTION } from "./template-projection";

const FlexibleProgramController = async ({
  res,
  req,
  locals,
  load,
  api,
  flash,
}) => {
  const { query = {}, params } = req;

  await load("editCycle", {
    cycle: params.cycleId,
    flexibleAthlete: query.athlete,
  });
  locals.query = query;

  const cycle = locals.editCycle;

  // The athlete-select dropdown (parts/header.js) only ever changes
  // ?athlete=... - it has no idea this page's own URL also pins a specific
  // cycle by id, so switching clients from here leaves params.cycleId
  // pointing at whoever's program was loaded before the switch.
  //
  // Resolved independently of `cycle` itself rather than compared against
  // it (e.g. via cycle.owner): several actions in actions/cycle.js and
  // actions/exercise.js stash their just-saved cycle into the session under
  // the SAME "editCycle" key with a short TTL, and the editCycle loader
  // returns that session value as-is when present - completely ignoring
  // which cycle id was actually requested. So `cycle` itself can't be
  // trusted as "the cycle params.cycleId asked for" close to a save. A
  // fresh, independent lookup of the selected athlete's own flexible
  // program can't have that problem.
  //
  // The dropdown is only ever used to pick a DIFFERENT athlete (there's no
  // "re-select who's already showing"), so whenever query.athlete is set,
  // this is always a genuine switch worth checking - no "is it actually the
  // same athlete" case to special-case.
  let theirCycle = null;
  if (query.athlete) {
    await load("athleteFlexibleProgram", { proxyAthlete: query.athlete });
    theirCycle = locals.athleteFlexibleProgram?.[0] || null;
  }
  const switchedToDifferentCycle =
    query.athlete && theirCycle?._id !== params.cycleId;

  if (!cycle || cycle.storeType !== "flexible" || switchedToDifferentCycle) {
    // organizationCoach has to survive the redirect: when the selected
    // athlete belongs to a different coach in the org, ensure-coach.js only
    // finds them in athletesToRequest when this is present (it's what pulls
    // in coachOrganizationInformation.athletes) - dropping it here would
    // bounce the coach to /coaching-portal/athletes instead.
    const athleteQuery = query.athlete
      ? `?athlete=${query.athlete}${query.organizationCoach ? `&organizationCoach=${query.organizationCoach}` : ""}`
      : "";
    if (theirCycle) {
      return res.redirect(
        `/athlete-portal/programs/flexible-program/${theirCycle._id}${athleteQuery}`
      );
    }
    // Never bounce silently: say why the manager could not be opened.
    flash("error", i18n.t("athlete-portal:flexible.errors.notFound"));
    return res.redirect(`/athlete-portal/programs${athleteQuery}`);
  }

  // Rows are AUTHORED WEEKS now, not projected occurrences (FlexibleProgramManager
  // picks each week's MOST RECENT occurrence for its progress/date columns) - so
  // this only ever needs to reach today, never beyond. Projecting ahead used to
  // make a repeating week's "latest occurrence" resolve to whatever future window
  // happened to sit at the far edge of the range, showing a date months ahead of
  // the client's actual current week.
  const weeks = (cycle.programs || [])
    .filter((program) => program.startDate)
    .sort((a, b) => a.startDate - b.startDate);

  if (weeks.length) {
    await load("flexibleProgramOverview", {
      cycleId: params.cycleId,
      from: weeks[0].startDate,
      to: Number(moment().format("YYYYMMDD")),
    });
  }

  // Templates the coach can import into this program (see ImportTemplateModal).
  // Owned by the coach themselves (not by a client), like the Templates page.
  await load("athleteCycles", {
    owner: locals.activeUserToRequest._id,
    customProjection: TEMPLATE_PROJECTION,
  });

  // Settings and Stop are now local-state modals owned by FlexibleProgram
  // itself (see FlexibleSettings.js) - only activity-feed still uses the
  // ?modal[type]=... convention.
  const modal = get(query, "modal", {});

  locals.modal = {
    "activity-feed": <ActivityDrawer />,
  }[modal.type];

  res.body = <FlexibleProgram locals={locals} api={api} />;
};

export default FlexibleProgramController;
